import React from "react";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Background } from "../../BuildingBlocks/Background";
import { BackgroundColour } from "../../BuildingBlocks/BackgroundColour";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../WeightBold/WeightBold";
import { CRMButtonPrimary } from "../../CRMButtonPrimary/CRMButtonPrimary";
import { CRMDropdownComponent } from "../../CRMDropdownComponent/CRMDropdownComponent";
import CRMInputGeneralComponent from "../../CRMInputs/CRMInputGeneralComponent/CRMInputGeneralComponent";
import CRMInputLabelAndErrorWrapComponent from "../../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import CRMTextAreaComponent from "../../CRMTextAreaComponent/CRMTextAreaComponent";
import { IOption } from "../../../hooks/UseDropdown";
import { TEmailTemplateForm } from "../../../../../domain/codecs/form/EmailTemplatesForm";
import { CaseSellerReasonForSale1 } from "../../../../../domain/codecs/CaseSellerReasonForSale";
import { CasesPurchasePaymentMethod1 } from "../../../../../domain/codecs/CasesPurchasePaymentMethod";
import { TransactionType1 } from "../../../../../domain/codecs/TransactionType";
import { getUnknownTokens } from "../../../../../domain/emailTemplates/tokens";

const ANY = "any";

const humanise = (value: string): string =>
    value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

// Every value the column accepts is offered, not just the two the seeded library uses. A
// dropdown listing a subset would quietly rewrite anything outside it to "any" the first
// time someone opened and saved that template.
const optionsWithAny = (values: ReadonlyArray<string>, anyLabel: string): Array<IOption<string>> => [
    { value: ANY, label: anyLabel },
    ...values.map((value) => ({ value, label: humanise(value) })),
];

const transactionTypeOptions = optionsWithAny(TransactionType1.values, "Any transaction");
const reasonForSaleOptions = optionsWithAny(CaseSellerReasonForSale1.values, "Any reason");
const paymentMethodOptions = optionsWithAny(CasesPurchasePaymentMethod1.values, "Any payment method");

// null is how "applies whatever the case is" is stored, and a dropdown cannot hold it. The
// lookup against the codec's own values is what types the result without an assertion.
const toCriterion = <A extends string>(values: ReadonlyArray<A>, value: string): A | null =>
    values.find((allowed) => allowed === value) ?? null;

const fromCriterion = (value: string | null): string => value === null ? ANY : value;

export const CRMEmailTemplateRow = (props: {
    form: TEmailTemplateForm,
    isOpen: boolean,
    onToggle: () => void,
    onChange: (form: TEmailTemplateForm) => void,
    onSave: (form: TEmailTemplateForm) => void,
}): JSX.Element => {
    const edit = (edited: Partial<TEmailTemplateForm["edited"]>) =>
        props.onChange({
            ...props.form,
            edited: { ...props.form.edited, ...edited },
        });

    const unknownTokens = getUnknownTokens(props.form.edited.body);
    const hasUnsavedChanges = props.form.status === "requiresSubmission";

    return (
        <BackgroundColour colour={CRMColors.NEUTRAL_12}>
            <Background padding={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <div onClick={props.onToggle}>
                        <SpacingRow spacing={CRMSpacing.MEDIUM} childSize="1fr 1fr 2fr">
                            <CRMParagraph>{props.form.edited.recipient || "(no recipient)"}</CRMParagraph>
                            <CRMParagraph>{props.form.edited.acting_for || "(any)"}</CRMParagraph>
                            <CRMParagraph>
                                <WeightBold>{props.form.edited.use_case || "(no use case)"}</WeightBold>
                            </CRMParagraph>
                        </SpacingRow>
                    </div>

                    {props.isOpen &&
                        <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                            <SpacingRow spacing={CRMSpacing.MEDIUM} childSize="1fr 1fr 1fr">
                                <CRMInputLabelAndErrorWrapComponent label="To">
                                    <CRMInputGeneralComponent
                                        value={props.form.edited.recipient}
                                        inputType="text"
                                        placeholder="Conveyancer"
                                        onChange={(recipient) => edit({ recipient })}
                                        displayError={false}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>

                                <CRMInputLabelAndErrorWrapComponent label="Acting for">
                                    <CRMInputGeneralComponent
                                        value={props.form.edited.acting_for}
                                        inputType="text"
                                        placeholder="Seller"
                                        onChange={(acting_for) => edit({ acting_for })}
                                        displayError={false}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>

                                <CRMInputLabelAndErrorWrapComponent label="Use case">
                                    <CRMInputGeneralComponent
                                        value={props.form.edited.use_case}
                                        inputType="text"
                                        placeholder="Chase contract"
                                        onChange={(use_case) => edit({ use_case })}
                                        displayError={false}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>
                            </SpacingRow>

                            <SpacingRow spacing={CRMSpacing.MEDIUM} childSize="1fr 1fr 1fr">
                                <CRMInputLabelAndErrorWrapComponent label="Offer on">
                                    <CRMDropdownComponent
                                        options={transactionTypeOptions}
                                        value={fromCriterion(props.form.edited.transaction_type)}
                                        displayError={false}
                                        onChange={(value) => edit({ transaction_type: toCriterion(TransactionType1.values, value) })}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>

                                <CRMInputLabelAndErrorWrapComponent label="Reason for sale">
                                    <CRMDropdownComponent
                                        options={reasonForSaleOptions}
                                        value={fromCriterion(props.form.edited.seller_reason_for_sale)}
                                        displayError={false}
                                        onChange={(value) => edit({ seller_reason_for_sale: toCriterion(CaseSellerReasonForSale1.values, value) })}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>

                                <CRMInputLabelAndErrorWrapComponent label="Payment method">
                                    <CRMDropdownComponent
                                        options={paymentMethodOptions}
                                        value={fromCriterion(props.form.edited.purchase_payment_method)}
                                        displayError={false}
                                        onChange={(value) => edit({ purchase_payment_method: toCriterion(CasesPurchasePaymentMethod1.values, value) })}
                                    />
                                </CRMInputLabelAndErrorWrapComponent>
                            </SpacingRow>

                            <CRMInputLabelAndErrorWrapComponent label="Body">
                                <CRMTextAreaComponent
                                    value={props.form.edited.body}
                                    placeholder="The body of the email"
                                    onChange={(body) => edit({ body })}
                                    displayError={unknownTokens.length > 0}
                                />
                            </CRMInputLabelAndErrorWrapComponent>

                            {unknownTokens.length > 0 &&
                                <CRMParagraph>
                                    <WeightBold>
                                        {unknownTokens.map((token) => `{{${token}}}`).join(", ")}
                                    </WeightBold>
                                    {unknownTokens.length === 1
                                        ? " is not a field we can fill in. Check the spelling against the list above, or remove it."
                                        : " are not fields we can fill in. Check the spelling against the list above, or remove them."
                                    }
                                </CRMParagraph>
                            }

                            {props.form.status === "validationError" &&
                                <CRMParagraph>
                                    This template still uses a field we cannot fill in, so it has not been saved.
                                </CRMParagraph>
                            }

                            {props.form.status === "failure" &&
                                <CRMParagraph>
                                    Something went wrong saving this template. Try again.
                                </CRMParagraph>
                            }

                            <div>
                                <CRMButtonPrimary
                                    label={props.form.status === "submitting" ? "Saving…" : "Save template"}
                                    disabled={!hasUnsavedChanges || unknownTokens.length > 0}
                                    fullWidth={false}
                                    onClick={() => props.onSave(props.form)}
                                />
                            </div>
                        </SpacingColumn>
                    }
                </SpacingColumn>
            </Background>
        </BackgroundColour>
    );
};
